import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import PerformanceMonitor from './components/PerformanceMonitor';
import OfflineSupport from './components/OfflineSupport';

import { MobileOptimizer } from './utils/mobileOptimizer';
import performanceManager from './utils/performanceManager';
import { PersonNavigationProvider } from './contexts/PersonNavigationContext';

// Import page components
import HomePage from './pages/HomePage';
import GenesPage from './pages/GenesPage';
import TravelPage from './pages/TravelPage';
import PlymouthPage from './pages/PlymouthPage';
import RawrockPage from './pages/RawrockPage';
import TomatoJarsPage from './pages/TomatoJarsPage';
import April122025Page from './pages/April122025Page';
import FutbolPage from './pages/FutbolPage';
import WeddingPage from './pages/WeddingPage';
import SouthingtonPage from './pages/SouthingtonPage';
import VinoPage from './pages/VinoPage';
import JeepPage from './pages/JeepPage';
import ThanksgivingPage from './pages/ThanksgivingPage';
import CalendarPage from './pages/CalendarPage';
import PanePage from './pages/PanePage';
import RossettiPage from './pages/RossettiPage';

import PersonDetailsPage from './pages/PersonDetailsPage';
import FamilyTreePage from './pages/FamilyTreePage';
import FamilyStatsPage from './pages/FamilyStatsPage';

// Recipe pages
import RecipeBrowserPage from './pages/RecipeBrowserPage';
import RecipeCategoryPage from './pages/RecipeCategoryPage';
import RecipeDetailPage from './pages/RecipeDetailPage';

// New pages
import Oct8_1989Page from './pages/Oct8_1989Page';
import Sept24_2008Page from './pages/Sept24_2008Page';
import Sept4_1982Page from './pages/Sept4_1982Page';
import YouTubePage from './pages/YouTubePage';
import BlockIslandPage from './pages/BlockIslandPage';
import ItalyPage from './pages/ItalyPage';
import ColombiaPage from './pages/ColombiaPage';
import FacebookPage from './pages/FacebookPage';
import TwitterPage from './pages/TwitterPage';
import InstagramPage from './pages/InstagramPage';
import LinkedInPage from './pages/LinkedInPage';

function PersonNavigationLayout() {
  return (
    <PersonNavigationProvider>
      <Outlet />
    </PersonNavigationProvider>
  );
}

function App() {
  // Initialize mobile optimizations and performance monitoring on app startup
  useEffect(() => {
    // Initialize mobile optimizations
    MobileOptimizer.initialize();
    
    // Track app initialization
    const appLoadingState = performanceManager.trackLoadingStart('app-initialization');
    
    // Simulate app initialization completion
    setTimeout(() => {
      performanceManager.trackLoadingEnd('app-initialization', true);
    }, 100);

    // Set up global error handling
    const handleUnhandledError = (event) => {
      performanceManager.trackError('global-error', event.error || event.reason, {
        type: 'unhandled-error',
        source: event.filename || 'unknown',
        line: event.lineno || 0,
        column: event.colno || 0
      });
    };

    const handleUnhandledRejection = (event) => {
      performanceManager.trackError('global-promise-rejection', event.reason, {
        type: 'unhandled-promise-rejection'
      });
    };

    // Add global error listeners
    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup
    return () => {
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary componentName="App">
        <OfflineSupport>
          <Router>
              {/* Performance monitoring in development */}
              {process.env.NODE_ENV === 'development' && <PerformanceMonitor />}
              
              <Layout>
                <Routes>
                  <Route path="/" element={
                    <ErrorBoundary componentName="HomePage">
                      <HomePage />
                    </ErrorBoundary>
                  } />
                  <Route path="/southington" element={
                    <ErrorBoundary componentName="SouthingtonPage">
                      <SouthingtonPage />
                    </ErrorBoundary>
                  } />
                  <Route element={<PersonNavigationLayout />}>
                    <Route path="/person/:personId" element={
                      <ErrorBoundary componentName="PersonDetailsPage">
                        <PersonDetailsPage />
                      </ErrorBoundary>
                    } />
                    <Route path="/family-tree" element={
                      <ErrorBoundary componentName="FamilyTreePage">
                        <FamilyTreePage />
                      </ErrorBoundary>
                    } />
                  </Route>
                  <Route path="/futbol" element={
                    <ErrorBoundary componentName="FutbolPage">
                      <FutbolPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/travel" element={
                    <ErrorBoundary componentName="TravelPage">
                      <TravelPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/calendar" element={
                    <ErrorBoundary componentName="CalendarPage">
                      <CalendarPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/pane" element={
                    <ErrorBoundary componentName="PanePage">
                      <PanePage />
                    </ErrorBoundary>
                  } />
                  <Route path="/rossetti" element={
                    <ErrorBoundary componentName="RossettiPage">
                      <RossettiPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/weddings" element={
                    <ErrorBoundary componentName="WeddingPage">
                      <WeddingPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/thanksgiving" element={
                    <ErrorBoundary componentName="ThanksgivingPage">
                      <ThanksgivingPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/family-stats" element={
                    <ErrorBoundary componentName="FamilyStatsPage">
                      <FamilyStatsPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/plymouth" element={
                    <ErrorBoundary componentName="PlymouthPage">
                      <PlymouthPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/rawrock" element={
                    <ErrorBoundary componentName="RawrockPage">
                      <RawrockPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/tomatojars" element={
                    <ErrorBoundary componentName="TomatoJarsPage">
                      <TomatoJarsPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/april-12-2025" element={
                    <ErrorBoundary componentName="April122025Page">
                      <April122025Page />
                    </ErrorBoundary>
                  } />
                  <Route path="/jeep" element={
                    <ErrorBoundary componentName="JeepPage">
                      <JeepPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/vino" element={
                    <ErrorBoundary componentName="VinoPage">
                      <VinoPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/genes" element={
                    <ErrorBoundary componentName="GenesPage">
                      <GenesPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/oct-8-1989" element={
                    <ErrorBoundary componentName="Oct8_1989Page">
                      <Oct8_1989Page />
                    </ErrorBoundary>
                  } />
                  <Route path="/sept-24-2008" element={
                    <ErrorBoundary componentName="Sept24_2008Page">
                      <Sept24_2008Page />
                    </ErrorBoundary>
                  } />
                  <Route path="/sept-4-1982" element={
                    <ErrorBoundary componentName="Sept4_1982Page">
                      <Sept4_1982Page />
                    </ErrorBoundary>
                  } />
                  <Route path="/youtube" element={
                    <ErrorBoundary componentName="YouTubePage">
                      <YouTubePage />
                    </ErrorBoundary>
                  } />
                  <Route path="/block-island" element={
                    <ErrorBoundary componentName="BlockIslandPage">
                      <BlockIslandPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/italy" element={
                    <ErrorBoundary componentName="ItalyPage">
                      <ItalyPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/colombia" element={
                    <ErrorBoundary componentName="ColombiaPage">
                      <ColombiaPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/facebook" element={
                    <ErrorBoundary componentName="FacebookPage">
                      <FacebookPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/twitter" element={
                    <ErrorBoundary componentName="TwitterPage">
                      <TwitterPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/instagram" element={
                    <ErrorBoundary componentName="InstagramPage">
                      <InstagramPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/linkedin" element={
                    <ErrorBoundary componentName="LinkedInPage">
                      <LinkedInPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/recipes" element={
                    <ErrorBoundary componentName="Recipe_Browser">
                      <RecipeBrowserPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/recipes/:category" element={
                    <ErrorBoundary componentName="Recipe_Category">
                      <RecipeCategoryPage />
                    </ErrorBoundary>
                  } />
                  <Route path="/recipes/:category/:slug" element={
                    <ErrorBoundary componentName="Recipe_Detail_View">
                      <RecipeDetailPage />
                    </ErrorBoundary>
                  } />
                  {/* Fallback route for 404 */}
                  <Route path="*" element={
                    <ErrorBoundary componentName="HomePage">
                      <HomePage />
                    </ErrorBoundary>
                  } />
                </Routes>
              </Layout>
            </Router>
        </OfflineSupport>
    </ErrorBoundary>
  );
}

export default App;
